import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

// Ultimo utente valido: permette di aprire l'app senza connessione (es. Test dell'udito offline)
const CACHE_KEY = "cached_user";
const remember = (u) => {
  try {
    if (u) localStorage.setItem(CACHE_KEY, JSON.stringify(u));
  } catch {
    /* storage non disponibile */
  }
};
const cachedUser = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
  } catch {
    return null;
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      // Only try the cookie-based refresh if we previously had a session
      // (avoids a guaranteed 401 + re-render for fresh anonymous visitors).
      if (!localStorage.getItem("had_session")) {
        setUser(null);
        return;
      }
      try {
        const { data } = await api.post("/auth/refresh", {});
        if (data?.token) localStorage.setItem("token", data.token);
        remember(data);
        setUser(data);
      } catch (e) {
        if (!e.response && cachedUser()) {
          setUser(cachedUser());
          return;
        }
        localStorage.removeItem("had_session");
        setUser(null);
      }
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      remember(data);
      setUser(data);
    } catch (e) {
      if (!e.response && cachedUser()) {
        setUser(cachedUser());
        return;
      }
      localStorage.removeItem("token");
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("token", data.token);
    localStorage.setItem("had_session", "1");
    remember(data);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed, clearing session locally", e);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("had_session");
    localStorage.removeItem(CACHE_KEY);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, logout, refresh }),
    [user, login, logout, refresh]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { formatApiError };
